'use client';

import { Gift, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCountdown } from '@/hooks/useCountdown';
import {
  isWithinCampaignWindow,
  supportsCampaignPlacement,
} from '@/config/site-campaigns';

function getDismissStorageKey(campaignId) {
  return `personal-offer:dismissed:${campaignId}`;
}

export default function PersonalizedOfferModal({
  config,
  pathname = '/',
  onClaim,
}) {
  const [mounted, setMounted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isActive = useMemo(() => (
    Boolean(
      config?.enabled
      && supportsCampaignPlacement(pathname, config?.placements)
      && isWithinCampaignWindow(config?.startAt, config?.endAt)
    )
  ), [config, pathname]);

  const countdown = useCountdown(config?.endAt, { enabled: isActive });

  useEffect(() => {
    setMounted(true);

    if (!config?.campaignId) {
      setDismissed(false);
      return;
    }

    try {
      const rawValue = window.localStorage.getItem(getDismissStorageKey(config.campaignId));
      if (!rawValue) {
        setDismissed(false);
        return;
      }

      if (Number(rawValue) > Date.now()) {
        setDismissed(true);
      } else {
        window.localStorage.removeItem(getDismissStorageKey(config.campaignId));
        setDismissed(false);
      }
    } catch (error) {
      console.error('Failed to restore personal offer modal state:', error);
      setDismissed(false);
    }
  }, [config]);

  if (!mounted || !isActive || dismissed) {
    return null;
  }

  const dismissModal = () => {
    if (config?.campaignId) {
      try {
        window.localStorage.setItem(
          getDismissStorageKey(config.campaignId),
          String(Date.now() + 24 * 60 * 60 * 1000)
        );
      } catch (error) {
        console.error('Failed to persist personal offer modal state:', error);
      }
    }

    setDismissed(true);
  };

  return createPortal(
    <div data-botgo-modal="personalized-offer" className="fixed inset-0 z-[140] flex items-center justify-center bg-black/78 px-4 backdrop-blur-md">
      <div className="relative w-full max-w-[620px] overflow-hidden rounded-[32px] border border-lime-400/10 bg-[#171515] shadow-[0_44px_120px_rgba(0,0,0,0.72)]">
        <button
          type="button"
          onClick={dismissModal}
          className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white/70 transition-colors hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="border-b border-white/6 px-6 py-5 text-center text-sm text-white/70">
          Congrats, you are in 5% who receives this offer
        </div>

        <div className="px-6 pb-7 pt-8 sm:px-10">
          <div className="mx-auto inline-flex h-24 w-24 items-center justify-center rounded-[28px] bg-lime-400 text-black shadow-[0_0_36px_rgba(163,230,53,0.35)]">
            <Gift className="h-12 w-12" />
          </div>

          <h2 className="mt-8 text-center text-4xl font-black uppercase leading-[1.12] text-white sm:text-5xl">
            {config?.heading}
          </h2>

          <p className="mx-auto mt-5 max-w-[430px] text-center text-base text-white/55">
            {config?.body}
          </p>

          <div className="mt-8 rounded-[28px] border border-white/6 bg-white/[0.03] p-4">
            <div className="rounded-2xl border border-lime-400/10 bg-[#1d1c1b] px-4 py-3 text-center text-sm text-lime-300">
              jack_PERSONAL_PROMO promocode is applied
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 rounded-[22px] bg-[#1b1a1a] p-5">
              <div className="rounded-2xl border border-white/6 bg-white/[0.02] px-4 py-5 text-center">
                <div className="text-4xl font-black text-lime-300">{config?.offerLabel}</div>
                <div className="mt-3 text-sm text-white/45">With limited-offer promo</div>
              </div>

              <div className="rounded-2xl border border-white/6 bg-white/[0.02] px-4 py-5 text-center">
                <div suppressHydrationWarning className="text-4xl font-black text-white">
                  {countdown.hours}:{countdown.minutes}:{countdown.seconds}
                </div>
                <div className="mt-3 text-sm text-white/45">hours minutes seconds</div>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClaim}
            className="mt-8 inline-flex w-full items-center justify-center rounded-2xl bg-lime-400 px-6 py-4 text-lg font-bold text-black shadow-[0_18px_42px_rgba(163,230,53,0.22)] transition-transform duration-200 hover:scale-[1.01]"
          >
            {config?.claimLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
